import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
    withStyles,
    Typography,
    Button,
    Link,
    Dialog,
    DialogTitle,
    DialogContent,
    CircularProgress
} from '@material-ui/core';
import { connect } from 'react-redux';
import { NavLink, Redirect } from 'react-router-dom';
import moment from 'moment';
import { RemoveCircleOutline, More, SettingsRemote, FindInPage } from '@material-ui/icons';
import RemoteAssetsList from './RemoteAssetsList';
import { hasPermission, formatDate, isSuperUser, createHistory } from '../../sc-utilities';
import { copyToClipBoard } from '../../sc-utilities';
import * as actions from './store/remoteAssetsActions';
import * as actionsClientType from '../UserActivity/store/userActivityActions';
import * as customerActions from '../../np-dashboard/CustomerBar/store/actions';
import StatisticModal from '../Statistic/StatisticModal'
import { snackActions } from '../../sc-utilities/SnackBar';
import appTranslations from '../../sc-translate/appTranslations';
import { getModel } from "../ProductOverview/utils";


const styles = theme => ({
    root: {
        width: `98.9%`,
        height: 'auto',
        paddingTop: theme.spacing(),
        paddingLeft: theme.spacing(),
        [theme.breakpoints.down('sm')]: {
            padding: 0,
            margin: 0,
            width: '100%'
        }
    },
    "@global": {
        "[class*='statusActive']": {
            color: theme.palette.success.dark
        },
        "[class*='statusInactive']": {
            color: theme.palette.error.dark
        },
        "[class*='button']": {
            textTransform: 'none',
            textDecoration: 'none',
            minWidth: '24px',
            borderRadius: '1px'
        },
    },
    button: {
        minWidth: '24px',
        borderRadius: '1px',
        textTransform: 'none',
        textDecoration: 'none'
    },
    statusActive: {
        color: theme.palette.success.dark
    },
    statusInactive: {
        color: theme.palette.error.dark
    },
    remote: {
        textDecoration: 'none',
        color: 'white'
    },
    detailsButton: {
        color: "initial",
        marginTop: theme.spacing()
    }
});

const OnlineDevices = props => {
    const tableRef = useRef();
    const { classes, translations, permissions, columnsVisible, onFetchClientTypes, activeCustomerID, removeDevice, onChangeActiveCustomer, activeCustomerName } = props;

    const [onlineDeviceInfo, setOnlineDeviceInfo] = useState({ open: false, onlineDeviceDialog: false });
    const [clientTypes, setClientTypes] = useState({});
    const [loading, setLoading] = useState(false);
    const [redirectOptions, setRedirectOptions] = useState({});


    useEffect(() => {
        const fetchClientTypes = async () => {
            setLoading(true);
            let clientTypeData = await onFetchClientTypes({ ActiveCustomerID: activeCustomerID });
            let clientTranslations = {};
            for (const key in clientTypeData) {
                if (clientTypeData.hasOwnProperty(key)) {
                    clientTranslations[key] = translations[`${key}`] || clientTypeData[key];
                }
            }
            setClientTypes(clientTranslations);
            setLoading(false);
        }
        fetchClientTypes();
        return () => { }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    let tableColumns = useMemo(() => {
        let visible = columnsVisible && columnsVisible['OnlineDevices'];

        return [
            {
                title: translations["customer_name"] || 'Customer name', field: 'CustomerName',
                hidden: visible ? visible['CustomerName'] : false,
                render: rowData => {
                    if (rowData.Customer && rowData.Customer.CustomerID) {
                        return <Button className={classes.button} color="primary" variant="outlined" component={Link} href={`/admin/customers/details?customerID=${rowData.CustomerID}`}><Typography variant={'caption'}>{rowData.Customer.Name}</Typography></Button>;
                    }
                    return <Typography variant={'caption'}>{translations['customer_not_detected'] || 'Customer not detected'}</Typography>
                }
            },
            {
                title: translations["client_type"] || 'Client type', field: 'ClientType',
                hidden: visible ? visible['ClientType'] : false,
                lookup: clientTypes,
                defaultFilter: Object.keys(clientTypes),
                render: rowData => {
                    return <Typography variant={'caption'}> {rowData.ClientType ? clientTypes[rowData.ClientType] : translations['short_not_assigned'] || 'n/a'}</Typography>
                }
            },
            {
                field: 'Vendor',
                title: translations['vendor'] || 'Vendor',
                hidden: visible ? visible['Vendor'] : false,
                render: rowData => {
                    return <Typography key={rowData.Product.ProductID} style={{ fontSize: 12 }}
                        gutterBottom>{rowData.Product.Vendor}</Typography>
                }
            },
            {
                title: translations["model"] || 'Model', field: 'Model', cellStyle: { minWidth: 250, maxWidth: 250 },
                hidden: visible ? visible['Model'] : false,
                filtering: false,
                render: rowData => {
                    return <Typography key={`${rowData.ProductID}_model`} style={{ fontSize: 12 }}
                        gutterBottom>{getModel(rowData.Product)}</Typography>
                }
            },
            {
                title: translations["client_version"] || 'Client version', field: 'ClientVersion',
                hidden: visible ? visible['ClientVersion'] : false,
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData.ClientVersion ? rowData.ClientVersion : translations['short_not_assigned'] || 'n/a'}</Typography>;
                }
            },
            {
                title: translations["hardware_id"] || 'Hardware ID', field: 'HardwareID',
                hidden: visible ? visible['HardwareID'] : false,
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData.HardwareID ? rowData.HardwareID : translations['short_not_assigned'] || 'n/a'}</Typography>;
                }
            },
            {
                title: translations["serial_number"] || 'Serial number', field: 'SerialNumber',
                hidden: visible ? visible['SerialNumber'] : false,
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData.SerialNumber ? rowData.SerialNumber : translations['short_not_assigned'] || 'n/a'}</Typography>;
                }
            },
            {
                title: translations["remote_erasure_activated"] || 'Remote erasure activated', field: 'RemoteErasureActivated',
                hidden: visible ? visible['RemoteErasureActivated'] : false,
                lookup: { '0': translations['no'] || 'No', '1': translations['yes'] || 'Yes' },
                defaultFilter: ['1'],
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData.RemoteErasureActivated ? translations['yes'] || 'Yes' : translations['no'] || 'No'}</Typography>;
                }
            },
            {
                title: translations["last_activity"] || 'Last activity', field: 'LastActivity',
                hidden: visible ? visible['LastActivity'] : false,
                type: 'datetime',
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData.LastActivity ? formatDate(rowData.LastActivity) : translations['short_not_assigned'] || 'n/a'}</Typography>;
                }
            }
        ]
    }, [clientTypes, classes, columnsVisible, translations]);

    const handleRemoveDevice = async (event, removeData) => {
        if (!event) {
            setOnlineDeviceInfo({ open: false });
            return;
        }
        setOnlineDeviceInfo({ ...onlineDeviceInfo, onlineDeviceDialog: true, DeviceOnlineStatusID: removeData.DeviceOnlineStatusID, HardwareID: removeData ? removeData.HardwareID : '' });
    };


    const handleCloseDanger = async () => {
        tableRef.current && tableRef.current.onQueryChange();
        await removeDevice({ DeviceOnlineStatusID: onlineDeviceInfo.DeviceOnlineStatusID, ActiveCustomerID: activeCustomerID })
        setOnlineDeviceInfo({ open: false });
    };

    const handleOpenAnydesk = async (event, rowData) => {
        copyToClipBoard(rowData.RemotePassword);
        snackActions.snackBarComponent((<Typography variant={'caption'} ><a className={classes.remote} rel="noopener noreferrer" href="https://anydesk.com/" target="_blank">
            {translations['securaze_remote_anydesk_snackbar_message'] || "Password is copied to clipboard. NOTE: Make sure you have installed AnyDesk"}
        </a></Typography>), 'info');
        const element = document.createElement('a');
        element.href = `anydesk:${rowData.RemoteID}`;
        element['data-href-alt'] = "https://anydesk.com/"
        return element.click();
    };

    const handleOpenRustdesk = async (event, rowData) => {
        console.log('rowData', rowData);
        copyToClipBoard(rowData.RemotePassword);
        snackActions.snackBarComponent((
            <Typography variant={'caption'}>
                <a
                    className={classes.remote}
                    rel="noopener noreferrer"
                    href="https://rustdesk.com/"
                    target="_blank"
                >
                    {translations['securaze_remote_rustdesk_snackbar_message'] || "Password is copied to clipboard. NOTE: Make sure you have installed RustDesk"}
                </a>
            </Typography>
        ), 'info');
        const element = document.createElement('a');
        element.href = `rustdesk://${rowData.RemoteID}`;
        element['data-href-alt'] = "https://rustdesk.com/";
        return element.click();
    };

    const productComponentHandler = useCallback(async (product, customer) => {
        createHistory();
        if (product?.CustomerID !== activeCustomerID) {
            setRedirectOptions({
                ...redirectOptions,
                open: true,
                product: customer?.Name,
                customerID: product?.CustomerID,
                redirectPath: `/pc-product/details?productID=${product?.ProductID}&type=${product?.Type}`
            });
            return;

        }
        setRedirectOptions({
            redirect: true,
            productID: product?.ProductID,
            redirectPath: `/pc-product/details?productID=${product?.ProductID}&type=${product?.Type}`,
        });
    }, [activeCustomerID, redirectOptions]);

    const handleChangeCustomer = async () => {
        await onChangeActiveCustomer(redirectOptions.customerID, redirectOptions.redirectPath);
    };

    const tableActions = [
        rowData => ({
            disabled: !rowData.ProductID,
            hidden: !hasPermission(permissions, 'product_load'),
            tooltip: translations['product_details'] || 'Product  details',
            onClick: (event, rowData) => productComponentHandler(rowData?.Product, rowData?.Customer),
            icon: () => {
                const activeCustomerParams = `&ActiveCustomerID=${rowData?.Customer?.CustomerID}&ActiveCustomerName=${rowData?.Customer?.Name}`;
                return (
                    <NavLink to={`/pc-product/details?productID=${rowData?.Product?.ProductID}&type=${rowData?.Product?.Type}${activeCustomerParams}`} className={classes.detailsButton}>
                        <FindInPage />
                    </NavLink>
                )
            },
        }),
        rowData => ({
            icon: More,
            tooltip: translations['online_device_details_information'] || 'Online device detail information',
            onClick: (event, rowData) => {
                let details = {
                    RemoteErasureActivated: rowData.RemoteErasureActivated ? translations['Yes'] || 'Yes' : translations['No'] || 'No',
                    HardwareID: rowData.HardwareID,
                    SerialNumber: rowData.SerialNumber,
                    FirstActivity: formatDate(rowData.FirstActivity),
                    LastActivity: rowData?.SocketID ? formatDate(moment()) : formatDate(rowData.LastActivity),
                    RemoteID: rowData.RemoteID || translations['short_not_assigned'] || 'n/a',
                    RemotePassword: rowData.RemotePassword || translations['short_not_assigned'] || 'n/a',
                    DefaultLanguage: rowData.DefaultLanguage || translations['short_not_assigned'] || 'n/a',
                    DefaultKeyboardLayout: rowData.DefaultKeyboardLayout || translations['short_not_assigned'] || 'n/a',
                    Username: rowData.User ? rowData.User.Username : translations['not_detected'] || 'Not detected',
                    CustomerName: rowData.Customer ? rowData.Customer.Name : translations['not_detected'] || 'Not detected'
                }
                setOnlineDeviceInfo({ ...onlineDeviceInfo, open: true, details: details });
            },
        }),
        rowData => ({
            icon: SettingsRemote,
            disabled: !rowData.RemoteID || !rowData.RemotePassword,
            tooltip: translations['open_remote_erasure_anydesk'] || 'Open Securaze Remote AnyDesk session',
            onClick: (event, rowData) => handleOpenAnydesk(event, rowData),
        }),
        rowData => ({
            icon: SettingsRemote,
            disabled: !rowData.RemoteID || !rowData.RemotePassword,
            tooltip: translations['open_remote_erasure_rustdesk'] || 'Open Securaze Remote RustDesk session',
            onClick: (event, rowData) => handleOpenRustdesk(event, rowData),
        }),
        rowData => ({
            icon: RemoveCircleOutline,
            tooltip: translations['remove_device'] || 'Remove device',
            onClick: (event, rowData) => handleRemoveDevice(event, rowData),
            hidden: !isSuperUser()
        }),
    ];

    if (redirectOptions.redirect) {
        return <Redirect to={redirectOptions.redirectPath} />;
    }

    const convertFullLanguageName = (language) => {
        const match = appTranslations.find(t => t.code === language);
        return match?.displayName || translations['short_not_assigned'] || 'n/a';
    }

    return (
        <div className={classes.root}>

            {redirectOptions.open ?
                <StatisticModal open={redirectOptions.open} activeCustomerName={activeCustomerName} product={redirectOptions.product} handleChangeCustomer={handleChangeCustomer} handleCloseSafe={() => setRedirectOptions({ ...redirectOptions, open: !redirectOptions.open })} translations={translations} activeCustomer={activeCustomerID} ></StatisticModal>
                : null}
            {hasPermission(permissions, 'device_online_status_list') && !loading ?
                <RemoteAssetsList handleRemoveDevice={handleRemoveDevice} handleCloseDanger={handleCloseDanger} onlineDeviceInfo={onlineDeviceInfo} tableColumns={tableColumns} tableActions={tableActions} tableRef={tableRef} /> : <CircularProgress />}
            {onlineDeviceInfo.open ?
                <Dialog
                    id="online-device-details"
                    open={onlineDeviceInfo.open}
                    disableBackdropClick={false}
                    disableEscapeKeyDown={false}
                    disableEnforceFocus={false}
                    fullWidth={true}
                    onClose={() => setOnlineDeviceInfo({ open: false })}
                >
                    <DialogTitle id="online-device-details">{translations['online_device_information'] || 'Online device information'}</DialogTitle>
                    <DialogContent dividers>
                        <Typography variant={'caption'} gutterBottom><b>{translations['remote_erasure_activated'] || 'Remote erasure activated'}: </b> {translations[onlineDeviceInfo.details.RemoteErasureActivated.toLowerCase()] || onlineDeviceInfo.details.RemoteErasureActivated}</Typography><br />
                        <Typography variant={'caption'} gutterBottom><b>{translations['hardware_id'] || 'HardwareID'}: </b> {onlineDeviceInfo.details.HardwareID}</Typography><br />
                        <Typography variant={'caption'} gutterBottom><b>{translations['serial_number'] || 'Serial number'}: </b> {onlineDeviceInfo.details.SerialNumber}</Typography><br />
                        <Typography variant={'caption'} gutterBottom><b>{translations['first_activity'] || 'First activity'}: </b> {onlineDeviceInfo.details.FirstActivity}</Typography><br />
                        <Typography variant={'caption'} gutterBottom><b>{translations['last_activity'] || 'Last activity'}: </b> {onlineDeviceInfo.details.LastActivity}</Typography><br />
                        <Typography variant={'caption'} gutterBottom><b>{translations['remote_id'] || 'Remote ID'}: </b> {onlineDeviceInfo.details.RemoteID}</Typography><br />
                        <Typography variant={'caption'} gutterBottom><b>{translations['remote_password'] || 'Remote password'}: </b> {onlineDeviceInfo.details.RemotePassword}</Typography><br />
                        <Typography variant={'caption'} gutterBottom><b>{translations['default_language'] || 'Default language'}: </b> {convertFullLanguageName(onlineDeviceInfo.details.DefaultLanguage)}</Typography><br />
                        <Typography variant={'caption'} gutterBottom><b>{translations['default_keyboard_layout'] || 'Default keyboard layout'}: </b> {convertFullLanguageName(onlineDeviceInfo.details.DefaultKeyboardLayout)}</Typography><br />
                        <Typography variant={'caption'} gutterBottom><b>{translations['logged_as'] || 'Logged as'}: </b> {onlineDeviceInfo.details.Username}</Typography><br />
                        <Typography variant={'caption'} gutterBottom><b>{translations['customer_name'] || 'Customer name'}: </b> {onlineDeviceInfo.details.CustomerName}</Typography><br />
                    </DialogContent>
                </Dialog>
                : null}
        </div >
    );
};

const mapStateToProps = state => {
    return {
        permissions: state?.core?.login?.permissions || [],
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        activeCustomerID: state.core.login.activeCustomer.CustomerID,
        activeCustomerName: state?.core?.login?.activeCustomer?.Name ?? '',
        columnsVisible: state.core.login.userSettings ? state.core.login.userSettings.Columns : null,
    };
};

const mapDispatchToProps = dispatch => {
    return {
        onFetchClientTypes: (data, option) => dispatch(actionsClientType.fetchClientTypes(data, option)),
        removeDevice: queryParams => dispatch(actions.removeDevice(queryParams)),
        onChangeActiveCustomer: (customerID, path) => dispatch(customerActions.changeActiveCustomer(customerID, path))

    }
};

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(withStyles(styles, { withTheme: true })(OnlineDevices));